import React, { useRef, useState, useEffect, useCallback, useMemo, useImperativeHandle, forwardRef } from "react";
import type { Clip, Track, Transform, Subtitle, SubtitleStyle, MontageVersion } from "../../types";
import Hls from 'hls.js';
import { createNewClip, generateThumbnails, generateWaveformData, getMediaDurationFromUrl, easeInOutCubic, formatDuration } from '../../utils';
import { getClipLocalSourceUrl, getClipPreviewUrl, isStreamingManifestUrl } from '../../utils/clip-helpers';
import { shiftAllSubtitleLanguages, filterAllSubtitleLanguages, fixSubtitleOverlaps } from '../../utils/subtitle-helpers';
import { AspectRatio } from "../../webcodecs-exporter";
import { SNAP_THRESHOLD_PX, TRACK_HEADER_WIDTH, TRANSFORM_SNAP_THRESHOLD, TRACK_HEIGHT_WITH_MARGIN, PREVIEW_RESOLUTION } from './constants';
import { WebGPURenderer } from '../../webgpu-renderer';
import { easeOutElastic, easeInBack } from './easing';
import './types';
import { PreviewArea } from './PreviewArea';
import { TimelineArea } from './TimelineArea';

const DEBUG_MEDIA = false;

// --- Main Component: TimelinePlayer ---
const TimelinePlayer = forwardRef(({
    onOpenExportMenu,
    subtitleStyle,
    activeSubtitleLanguage,
    onActiveSubtitleLanguageChange,
    onVideoFileDropped,
    onEnhanceClipAudio,
    onSelectAudioSource,
    onClipSelect,
    tracks,
    setTracks,
    allClips,
    onClipsChange,
    selectedClipId,
    setSelectedClipId,
    onTimeUpdate,
    historyIndex,
    historyLength,
    undo,
    redo,
    aspectRatio,
    onAspectRatioChange,
    isExporting = false,
    montageVersions = [],
    currentVersionId = null,
    onSelectVersion = () => { },
    onNavigateInto,
    onNavigateUp,
    canNavigateUp,
    navigationBreadcrumbs,
    scaleMode = 'fit',
    onScaleModeChange = () => { },
    onSaveProject,
    onTranslateSubtitles,
    onSubtitleClick,
    onTextOverlayDoubleClick,
    isRestrictedMode = false,
    projectLoadingState,
    onGenerateSubtitles,
    translationsInProgress,
}: {
    onOpenExportMenu: () => void,
    subtitleStyle: SubtitleStyle,
    activeSubtitleLanguage: string,
    onActiveSubtitleLanguageChange: (lang: string) => void,
    onVideoFileDropped?: (file: File, clip: Clip) => void,
    onEnhanceClipAudio?: (clip: Clip) => void,
    onSelectAudioSource?: (clip: Clip, sourceType: 'original' | 'enhanced') => void,
    onClipSelect?: (clip: Clip | null) => void,
    onTextOverlayDoubleClick?: () => void,
    tracks: Track[],
    setTracks: React.Dispatch<React.SetStateAction<Track[]>>,
    allClips: Clip[],
    onClipsChange: (updater: React.SetStateAction<Clip[]>, saveToHistory?: boolean) => void,
    selectedClipId: string | null,
    setSelectedClipId: (id: string | null) => void,
    onTimeUpdate?: (time: number) => void;
    historyIndex: number;
    historyLength: number;
    undo: () => void;
    redo: () => void;
    aspectRatio: AspectRatio;
    onAspectRatioChange: (ratio: AspectRatio) => void;
    isExporting?: boolean;
    montageVersions?: MontageVersion[];
    currentVersionId?: string | null;
    onSelectVersion?: (id: string) => void;
    onNavigateInto?: (clip: Clip) => void;
    onNavigateUp?: () => void;
    canNavigateUp?: boolean;
    navigationBreadcrumbs?: string[];
    scaleMode?: 'fit' | 'fill';
    onScaleModeChange?: (mode: 'fit' | 'fill') => void;
    onSaveProject?: () => void;
    onTranslateSubtitles?: (targetLang: string) => void;
    onSubtitleClick?: (subtitle: Subtitle) => void;
    isRestrictedMode?: boolean;
    projectLoadingState?: { isLoading: boolean; thumbnailUrl?: string };
    onGenerateSubtitles?: (clip: Clip) => void;
    translationsInProgress?: Set<string>;
}, ref) => {
    // State
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [zoom, setZoom] = useState(isRestrictedMode ? 50 : 10);
    const [duration, setDuration] = useState(0);
    const [isScrubbing, setIsScrubbing] = useState(false);
    // isHoveringStage removed, moved to PreviewArea
    const [isSeekbarScrubbing, setIsSeekbarScrubbing] = useState(false);
    const [timelineGrabState, setTimelineGrabState] = useState<{ startX: number, startScrollLeft: number, hasMoved: boolean } | null>(null);
    const [isPlayheadDragging, setIsPlayheadDragging] = useState(false);
    const [isGlobalMuted, setIsGlobalMuted] = useState(false); // Global Audio Toggle


    const [isTransforming, setIsTransforming] = useState<{
        action: string;
        handle?: string;
        startX: number;
        startY: number;
        initialTransform: Transform;
        clipRect: DOMRect;
        centerX?: number;
        centerY?: number;
        initialDistance?: number;
        initialAngleRad?: number;
    } | null>(null);
    const [canvasDimensions, setCanvasDimensions] = useState<{ width: number, height: number, clipBounds: null | DOMRect, scale: number }>({ width: 0, height: 0, clipBounds: null, scale: 1 });
    const [clipDragState, setClipDragState] = useState<{ action: string, clipId: string, startX: number, initialClip: Clip } | null>(null);
    const [trackDragState, setTrackDragState] = useState<{ id: string } | null>(null);
    const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
    const [toolMode, setToolMode] = useState<'move' | 'cut'>('move');
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [isBuffering, setIsBuffering] = useState(false);
    const [isSmileyPopupOpen, setIsSmileyPopupOpen] = useState(false);

    // Force default zoom in restricted mode
    useEffect(() => {
        if (isRestrictedMode) {
            setZoom(50);
            setToolMode('move'); // Force move tool
        }
    }, [isRestrictedMode]);

    // UI State for editing text (passed to PreviewArea)
    const [editingTextClipId, setEditingTextClipId] = useState<string | null>(null);
    const [editingTextValue, setEditingTextValue] = useState('');
    const [editingFontFamily, setEditingFontFamily] = useState('Arial, sans-serif');

    // Layout State
    const [previewSectionHeight, setPreviewSectionHeight] = useState<number | null>(null); // null = 60% default

    const normalizedSubtitlesByClip = useMemo(() => {
        const map = new Map<string, Subtitle[]>();
        if (activeSubtitleLanguage === 'none') return map;

        for (const clip of allClips) {
            const langSubs = clip.subtitles?.[activeSubtitleLanguage];
            if (!Array.isArray(langSubs) || langSubs.length === 0) continue;
            map.set(clip.id, fixSubtitleOverlaps(langSubs, 0.08));
        }
        return map;
    }, [allClips, activeSubtitleLanguage]);

    const getActiveSubtitleAtTime = useCallback((time: number, clipsScope: Clip[]) => {
        if (activeSubtitleLanguage === 'none') return null;

        const boundaryEpsilon = 0.02;
        let winner: Subtitle | null = null;

        for (const clip of clipsScope) {
            const subs = normalizedSubtitlesByClip.get(clip.id);
            if (!subs) continue;

            for (const sub of subs) {
                if (sub.hidden) continue;

                const effectiveEnd = Math.max(sub.start + 0.02, sub.end - boundaryEpsilon);
                if (time + boundaryEpsilon >= sub.start && time < effectiveEnd) {
                    // In ambiguous boundary cases, always keep a single, most recent subtitle.
                    if (!winner || sub.start > winner.start || (sub.start === winner.start && sub.end < winner.end)) {
                        winner = sub;
                    }
                }
            }
        }

        return winner;
    }, [activeSubtitleLanguage, normalizedSubtitlesByClip]);

    // Refs
    const containerRef = useRef<HTMLDivElement>(null);
    const timelineScrollContainerRef = useRef<HTMLDivElement>(null);
    const timelineContentRef = useRef<HTMLDivElement>(null);
    const trackHeadersRef = useRef<HTMLDivElement>(null);
    const tracksContainerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const videoRefs = useRef<{ [key: string]: HTMLVideoElement }>({});
    const imageRefs = useRef<{ [key: string]: HTMLImageElement }>({});
    const audioElementsRef = useRef<{ [key: string]: HTMLAudioElement }>({});
    const audioContextRef = useRef<AudioContext | null>(null);
    const seekbarRef = useRef<HTMLDivElement>(null); // Still needed? Maybe pass to TimelineArea if ruler needs it
    const webgpuRendererRef = useRef<WebGPURenderer | null>(null);
    const playheadRef = useRef<HTMLDivElement>(null);
    const zoomRef = useRef(zoom);
    const playbackStartOffset = useRef(0);
    const playbackStartTime = useRef(0);
    const isPlayingRef = useRef(isPlaying);
    const durationRef = useRef(duration);
    const currentTimeRef = useRef(currentTime);
    const playbackSpeedRef = useRef(playbackSpeed);
    const nestedImageCache = useRef<{ [key: string]: HTMLImageElement }>({});
    const hlsInstances = useRef<{ [key: string]: Hls }>({});

    // Sync refs
    useEffect(() => { zoomRef.current = zoom; }, [zoom]);

    // Sync refs & Handle Play/Pause Timing
    useEffect(() => {
        isPlayingRef.current = isPlaying;
        if (isPlaying) {
            playbackStartTime.current = Date.now();
            playbackStartOffset.current = currentTimeRef.current;
        }
    }, [isPlaying]);

    // Handle Playback Speed Reset
    useEffect(() => {
        playbackSpeedRef.current = playbackSpeed;
        if (isPlaying) {
            // Reset timing anchor on speed change to prevent jumps
            playbackStartTime.current = Date.now();
            playbackStartOffset.current = currentTimeRef.current;
        }
    }, [playbackSpeed, isPlaying]);
    useEffect(() => { durationRef.current = duration; }, [duration]);
    useEffect(() => { durationRef.current = duration; }, [duration]);
    useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);

    // Initialize AudioContext for enhanced audio routing (WebAudio API)
    useEffect(() => {
        const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
            const ctx = new AudioContextClass();
            audioContextRef.current = ctx;
            if (DEBUG_MEDIA) console.log('[Player] AudioContext initialized:', ctx.state);
        }

        // Resume on interaction
        const resumeAudio = () => {
            const ctx = audioContextRef.current;
            if (ctx && ctx.state === 'suspended') {
                ctx.resume().then(() => {
                    if (DEBUG_MEDIA) console.log('[Player] AudioContext resumed');
                });
            }
        };
        window.addEventListener('mousedown', resumeAudio);
        window.addEventListener('keydown', resumeAudio);
        return () => {
            window.removeEventListener('mousedown', resumeAudio);
            window.removeEventListener('keydown', resumeAudio);
            if (audioContextRef.current) audioContextRef.current.close();
        };
    }, []);

    // Global Mute controls
    useEffect(() => {
        // For video elements, respect enhanced audio — keep muted if enhanced audio is active
        allClips.forEach(clip => {
            if (clip.type === 'video') {
                const videoEl = videoRefs.current[clip.id];
                if (!videoEl) return;
                const hasEnhancedAudio = clip.activeAudioSource === 'enhanced' && clip.audioSources?.some(s => s.type === 'enhanced');
                const enhancedAudioKey = `enhanced-${clip.id}`;
                const enhancedEl = audioElementsRef.current[enhancedAudioKey];
                const enhancedReady = enhancedEl && enhancedEl.readyState >= 2;
                // Only mute video for enhanced audio when enhanced is actually ready
                videoEl.muted = (hasEnhancedAudio && enhancedReady) ? true : isGlobalMuted;
            }
        });
        Object.values(audioElementsRef.current).forEach((el: any) => { if (el) el.muted = isGlobalMuted; });
    }, [isGlobalMuted, allClips]);

    // Cleanup removed clips' audio elements
    useEffect(() => {
        const currentClipIds = new Set(allClips.map(c => c.id));
        Object.keys(audioElementsRef.current).forEach(key => {
            // key can be an audio clip ID, or `enhanced-${clipId}`
            const isEnhanced = key.startsWith('enhanced-');
            const clipId = isEnhanced ? key.replace('enhanced-', '') : key;

            if (!currentClipIds.has(clipId)) {
                // Clip was deleted from timeline, destroy its audio element
                const el = audioElementsRef.current[key];
                if (el) {
                    el.pause();
                    el.src = '';
                    if (el.parentElement) el.remove();
                }
                delete audioElementsRef.current[key];
                console.log(`[Player] Destroyed audio element for removed clip: ${key}`);
            }
        });
    }, [allClips]);

    const tracksById = useRef<Record<string, Track>>({});
    useEffect(() => {
        tracksById.current = Object.fromEntries(tracks.map(track => [track.id, track]));
    }, [tracks]);

    const getEffectiveClipVolume = useCallback((clip: Clip) => {
        const track = tracksById.current[clip.trackId];
        if (track?.isMuted) return 0;
        const clipVolume = Math.min(1, Math.max(0, clip.volume ?? 1));
        const trackVolume = Math.min(1, Math.max(0, track?.volume ?? 1));
        return Math.min(1, Math.max(0, clipVolume * trackVolume));
    }, []);

    // Sync Video Elements: Play/Pause, Seek & Speed
    useEffect(() => {
        // ONE-TIME diagnostic when playback starts: show audio config for all clips
        if (isPlaying && !(window as any).__enhancedAudioDiagDone) {
            (window as any).__enhancedAudioDiagDone = true;
            console.log(`[Player] 🔍 AUDIO CONFIG DUMP (${allClips.length} clips):`);
            allClips.forEach(clip => {
                if (clip.type === 'video') {
                    console.log(`  📎 ${clip.name} (${clip.id.slice(0, 8)}): activeAudioSource=${clip.activeAudioSource ?? 'UNSET'}, audioSources=${clip.audioSources ? clip.audioSources.map(s => `${s.type}:${s.url?.slice(0, 50)}`).join(', ') : 'NONE'}`);
                }
            });
            // Reset flag after 10 seconds to allow re-dump if needed
            setTimeout(() => { (window as any).__enhancedAudioDiagDone = false; }, 10000);
        }

        allClips.forEach(clip => {
            if (clip.type === 'video') {
                const videoEl = videoRefs.current[clip.id];
                if (!videoEl) return;

                // Sync base volume and playback rate instantly to react to slider changes
                const targetVolume = getEffectiveClipVolume(clip);
                if (videoEl.volume !== targetVolume) {
                    videoEl.volume = targetVolume;
                }
                const targetSpeed = (clip.playbackRate ?? 1) * playbackSpeed;
                if (videoEl.playbackRate !== targetSpeed) {
                    videoEl.playbackRate = targetSpeed;
                }

                // Enhanced audio support: when enhanced audio is active, mute video and play separate audio
                const hasEnhancedAudio = clip.activeAudioSource === 'enhanced' && clip.audioSources;
                const enhancedSource = hasEnhancedAudio ? clip.audioSources!.find(s => s.type === 'enhanced') : null;
                const enhancedAudioKey = `enhanced-${clip.id}`;

                if (enhancedSource) {
                    // Only mute video when enhanced audio is actually ready to play
                    // Otherwise keep video audio as fallback
                    const existingEnhancedEl = audioElementsRef.current[enhancedAudioKey];
                    const enhancedReady = existingEnhancedEl && existingEnhancedEl.readyState >= 2;
                    videoEl.muted = enhancedReady ? true : isGlobalMuted;

                    // Create enhanced audio element if needed
                    let enhancedAudioEl = audioElementsRef.current[enhancedAudioKey];
                    if (!enhancedAudioEl || (enhancedAudioEl as any)._enhancedUrl !== enhancedSource.url) {
                        // Clean up old one if URL changed
                        if (enhancedAudioEl) {
                            enhancedAudioEl.pause();
                            if (enhancedAudioEl.parentElement) enhancedAudioEl.remove(); // Cleanup DOM
                            delete audioElementsRef.current[enhancedAudioKey];
                        }
                        enhancedAudioEl = new (window as any).Audio();
                        enhancedAudioEl.crossOrigin = 'anonymous';
                        // enhancedAudioEl.preload = 'auto'; // Disable aggressive preload to prevent network choke
                        enhancedAudioEl.src = enhancedSource.url;
                        enhancedAudioEl.muted = isGlobalMuted;
                        (enhancedAudioEl as any)._enhancedUrl = enhancedSource.url;
                        document.body.appendChild(enhancedAudioEl); // FORCE ATTACH TO DOM to avoid GC/Policies

                        // Connect to WebAudio Graph if available
                        if (audioContextRef.current) {
                            try {
                                const source = audioContextRef.current.createMediaElementSource(enhancedAudioEl);
                                source.connect(audioContextRef.current.destination);
                                console.log(`[Player] 🔌 Connected ${clip.name} to AudioContext`);
                            } catch (err) {
                                console.warn(`[Player] ⚠️ Failed to connect to AudioContext:`, err);
                            }
                        }

                        audioElementsRef.current[enhancedAudioKey] = enhancedAudioEl;
                        // Debug: track loading issues
                        enhancedAudioEl.addEventListener('error', (e: any) => {
                            const err = enhancedAudioEl.error;
                            console.error(`[Player] ❌ Enhanced audio LOAD ERROR for ${clip.name}:`, err?.code, err?.message);
                        });
                        enhancedAudioEl.addEventListener('stalled', () => console.warn(`[Player] ⚠️ Enhanced audio STALLED for ${clip.name}`));
                        enhancedAudioEl.addEventListener('waiting', () => console.warn(`[Player] ⏳ Enhanced audio BUFFERING/WAITING for ${clip.name}`));
                        enhancedAudioEl.addEventListener('canplay', () => {
                            if (!(enhancedAudioEl as any)._canPlayLogged) {
                                console.log(`[Player] ✅ Enhanced audio CAN PLAY for ${clip.name} (readyState: ${enhancedAudioEl.readyState})`);
                                (enhancedAudioEl as any)._canPlayLogged = true;
                            }
                        });
                        console.log(`[Player] Created enhanced audio element for ${clip.name}: ${enhancedSource.url}`);
                    }
                } else {
                    // Original audio: un-mute video, clean up enhanced audio element
                    if (!isGlobalMuted) videoEl.muted = false;
                    const existingEnhanced = audioElementsRef.current[enhancedAudioKey];
                    if (existingEnhanced) {
                        existingEnhanced.pause();
                        delete audioElementsRef.current[enhancedAudioKey];
                    }
                }

                const isClipActive = currentTime >= clip.start && currentTime < clip.start + clip.duration;
                const clipRate = clip.playbackRate ?? 1;

                // Audio and Video might have different time bases:
                // - Enhanced Audio / Enhanced Video: Pre-trimmed (start from 0)
                // - Original Video: Full source (start from trimStart)

                const isEnhancedVideo = clip.activeVideoVersion && clip.activeVideoVersion !== 'original';
                const isEnhancedAudio = !!enhancedSource;

                // Video Offset: Only ignore trimStart if the VIDEO itself is enhanced (pre-trimmed)
                // If we are playing Original Video + Enhanced Audio, Video MUST respect trimStart
                const videoStartOffset = isEnhancedVideo ? 0 : (clip.trimStart || 0);

                // Audio Offset: Enhanced audio is always pre-trimmed
                const audioStartOffset = 0;

                // Calculate relative times
                const videoRelativeTime = (currentTime - clip.start) * clipRate + videoStartOffset;
                const audioRelativeTime = (currentTime - clip.start) * clipRate + audioStartOffset;

                // Sync volume from clip data
                const effectiveClipVolume = getEffectiveClipVolume(clip);
                if (videoEl.volume !== effectiveClipVolume) {
                    videoEl.volume = effectiveClipVolume;
                }

                // Sync playback rate: per-clip rate * global player speed
                const effectiveRate = clipRate * playbackSpeed;
                if (videoEl.playbackRate !== effectiveRate) {
                    videoEl.playbackRate = effectiveRate;
                }

                // HLS / Chunk Logic uses VIDEO time
                let targetTime = videoRelativeTime;
                const localSourceUrl = getClipLocalSourceUrl(clip);

                if (localSourceUrl) {
                    if (hlsInstances.current[clip.id]) {
                        hlsInstances.current[clip.id].destroy();
                        delete hlsInstances.current[clip.id];
                        videoEl.removeAttribute('src');
                    }

                    const currentDirectUrl = videoEl.currentSrc || videoEl.src;
                    if (currentDirectUrl !== localSourceUrl) {
                        videoEl.src = localSourceUrl;
                    }
                } else if (clip.chunks && clip.chunks.length > 0 && Hls.isSupported()) {
                    // Find active chunk using VIDEO time
                    const activeChunk = clip.chunks.find(c => videoRelativeTime >= c.startTime && videoRelativeTime < c.startTime + c.duration);

                    if (activeChunk) {
                        // Check if we need to switch chunks
                        const isHlsChunk = isStreamingManifestUrl(activeChunk.streamUrl);

                        if (isHlsChunk) {
                            let hls = hlsInstances.current[clip.id];
                            if (!hls) {
                                hls = new Hls({ enableWorker: true, lowLatencyMode: true });
                                hlsInstances.current[clip.id] = hls;
                                hls.attachMedia(videoEl);
                            }

                            // We store the current URL on the HLS instance to track it
                            const currentUrl = (hls as any).currentStreamUrl;
                            if (currentUrl !== activeChunk.streamUrl) {
                                console.log(`[TimelinePlayer] Switching to HLS chunk: ${activeChunk.streamUrl}`);
                                hls.loadSource(activeChunk.streamUrl);
                                (hls as any).currentStreamUrl = activeChunk.streamUrl;
                            }
                        } else {
                            // MP4 Fallback (R2) - Direct Playback
                            if (hlsInstances.current[clip.id]) {
                                // If we were using HLS, destroy it for this clip
                                hlsInstances.current[clip.id].destroy();
                                delete hlsInstances.current[clip.id];
                                videoEl.removeAttribute('src');
                            }

                            // Check if src needs update (handling full URL comparison)
                            // Note: videoEl.currentSrc returns full absolute URL
                            if (!videoEl.src.includes(activeChunk.streamUrl)) {
                                console.log(`[TimelinePlayer] Switching to MP4 chunk: ${activeChunk.streamUrl}`);
                                videoEl.src = activeChunk.streamUrl;
                            }
                        }

                        // Adjust time to be relative to the chunk start
                        // The video element thinks it's playing a shorter video (the chunk)
                        targetTime = videoRelativeTime - activeChunk.startTime;
                    }
                } else if (clip.streamUrl && isStreamingManifestUrl(clip.streamUrl) && Hls.isSupported()) {
                    // Single HLS Stream
                    let hls = hlsInstances.current[clip.id];
                    if (!hls) {
                        hls = new Hls({ enableWorker: true, lowLatencyMode: true });
                        hlsInstances.current[clip.id] = hls;
                        hls.attachMedia(videoEl);
                    }
                    const currentUrl = (hls as any).currentStreamUrl;
                    if (currentUrl !== clip.streamUrl) {
                        hls.loadSource(clip.streamUrl);
                        (hls as any).currentStreamUrl = clip.streamUrl;
                    }
                }

                if (isClipActive) {
                    // Log when an enhanced-audio clip becomes active (one-time per activation)
                    if (enhancedSource && isPlaying) {
                        const activationKey = `_active_${clip.id}`;
                        if (!(window as any)[activationKey]) {
                            (window as any)[activationKey] = true;
                            const eAudioEl = audioElementsRef.current[enhancedAudioKey];
                            console.log(`[Player] 🎯 Enhanced clip "${clip.name}" (${clip.id.slice(0, 8)}) ACTIVE at t=${currentTime.toFixed(2)}, range=[${clip.start.toFixed(2)}-${(clip.start + clip.duration).toFixed(2)}]`);
                            if (eAudioEl) {
                                console.log(`[Player] 🔊 State: muted=${eAudioEl.muted}, vol=${eAudioEl.volume}, paused=${eAudioEl.paused}, ready=${eAudioEl.readyState}, time=${eAudioEl.currentTime.toFixed(2)}/${eAudioEl.duration?.toFixed(2)}`);
                                console.log(`[Player] 🔗 Enhanced Source URL: ${eAudioEl.src}`);

                                // FORCE PLAY: directly play enhanced audio on activation
                                eAudioEl.muted = false;
                                eAudioEl.volume = getEffectiveClipVolume(clip);
                                // Enhanced audio is pre-trimmed, always start relative to 0
                                eAudioEl.currentTime = Math.max(0, audioRelativeTime);
                                videoEl.muted = true;
                                eAudioEl.play().then(() => {
                                    console.log(`[Player] ✅ FORCE PLAY: Enhanced audio NOW PLAYING for "${clip.name}"`);
                                    // Monitor playback progress
                                    const monitorInterval = setInterval(() => {
                                        if (eAudioEl.paused || !(window as any)[activationKey]) clearInterval(monitorInterval);
                                        else console.log(`[Player] 📊 Audio Progress: ${eAudioEl.currentTime.toFixed(3)} / ${eAudioEl.duration?.toFixed(3)}`);
                                    }, 250);
                                }).catch(e => {
                                    console.warn(`[Player] ❌ FORCE PLAY FAILED for "${clip.name}":`, e.message);
                                });
                            } else {
                                console.warn(`[Player] ❌ Enhanced audio element NOT FOUND for key: ${enhancedAudioKey}`);
                            }
                            setTimeout(() => { (window as any)[activationKey] = false; }, (clip.duration + 1) * 1000);
                        }
                    }

                    // How much time remains in this clip
                    const clipTimeRemaining = (clip.start + clip.duration) - currentTime;

                    if (!isPlaying) {
                        if (!videoEl.error && Math.abs(videoEl.currentTime - targetTime) > 0.1) {
                            if (videoEl.readyState >= 1) { // Only seek if metadata loaded to prevent PIPELINE_ERROR_READ
                                videoEl.currentTime = Math.max(0, targetTime);
                            }
                        }
                        if (!videoEl.paused && !videoEl.error) videoEl.pause();
                    } else {
                        if (!videoEl.error) {
                            const timeDiff = Math.abs(videoEl.currentTime - targetTime);
                            if (timeDiff > 0.5) {
                                if (videoEl.readyState >= 1) { // Only seek if metadata loaded
                                    videoEl.currentTime = Math.max(0, targetTime);
                                }
                            }
                        }

                        if (isBuffering) {
                            if (!videoEl.paused && !videoEl.error) videoEl.pause();
                        } else {
                            // FIX: Don't call play() if clip is about to end (< 100ms remaining)
                            // This prevents the "play() interrupted by pause()" race condition
                            if (videoEl.paused && !videoEl.error && clipTimeRemaining > 0.1) {
                                videoEl.play().catch(() => {
                                    // Silently ignore — this is expected at clip boundaries
                                });
                            }
                        }
                    }

                    // Sync enhanced audio element if present
                    if (enhancedSource) {
                        const enhancedAudioEl = audioElementsRef.current[enhancedAudioKey];
                        if (enhancedAudioEl) {
                            // Always sync volume, mute, and rate
                            const enhancedVolume = getEffectiveClipVolume(clip);
                            if (enhancedAudioEl.volume !== enhancedVolume) enhancedAudioEl.volume = enhancedVolume;
                            enhancedAudioEl.muted = isGlobalMuted;

                            const enhancedEffectiveRate = (clip.playbackRate ?? 1) * playbackSpeed;
                            if (enhancedAudioEl.playbackRate !== enhancedEffectiveRate) {
                                enhancedAudioEl.playbackRate = enhancedEffectiveRate;
                            }

                            if (isPlaying) {
                                // Ensure video is muted when enhanced audio is active
                                if (!videoEl.error && !videoEl.muted) {
                                    videoEl.muted = true;
                                }

                                // Sync time only if drift is significant and audio is ready
                                if (enhancedAudioEl.readyState >= 1) {
                                    const audioDrift = Math.abs(enhancedAudioEl.currentTime - audioRelativeTime);
                                    if (audioDrift > 0.5) {
                                        enhancedAudioEl.currentTime = Math.max(0, audioRelativeTime);
                                    }
                                }

                                // Play if paused and ready — don't interfere if already playing
                                if (enhancedAudioEl.paused && enhancedAudioEl.readyState >= 2) {
                                    console.log(`[Player] ▶️ Playing enhanced audio for "${clip.name}"`);
                                    enhancedAudioEl.play().catch(e => {
                                        console.warn(`[Player] ❌ Enhanced play failed:`, e.message);
                                    });
                                }
                            } else {
                                // Pause when not playing
                                if (!enhancedAudioEl.paused) {
                                    enhancedAudioEl.pause();
                                }
                            }
                        } else if (isPlaying) {
                            console.warn(`[Player] ⚠️ Enhanced source active for "${clip.name}" but no audio element found!`);
                        }
                    }
                } else {
                    if (!videoEl.paused) {
                        videoEl.pause();
                    }
                    // Pause enhanced audio when clip is inactive
                    if (enhancedSource) {
                        const enhancedAudioEl = audioElementsRef.current[enhancedAudioKey];
                        if (enhancedAudioEl && !enhancedAudioEl.paused) enhancedAudioEl.pause();
                    }
                }
            }

            // Sync Audio Clips (Voiceovers, etc.)
            if (clip.type === 'audio' && clip.url) {
                let audioEl = audioElementsRef.current[clip.id];

                // Create audio element if it doesn't exist
                if (!audioEl) {
                    audioEl = new (window as any).Audio(clip.url);
                    audioEl.preload = 'metadata';
                    audioEl.muted = isGlobalMuted;
                    audioElementsRef.current[clip.id] = audioEl;
                }

                // Sync base volume and playback rate instantly to react to slider changes
                const targetVolume = getEffectiveClipVolume(clip);
                if (audioEl.volume !== targetVolume) {
                    audioEl.volume = targetVolume;
                }
                const targetSpeed = (clip.playbackRate ?? 1) * playbackSpeed;
                if (audioEl.playbackRate !== targetSpeed) {
                    audioEl.playbackRate = targetSpeed;
                }

                const isClipActive = currentTime >= clip.start && currentTime < clip.start + clip.duration;
                const relativeTime = currentTime - clip.start + (clip.trimStart || 0);

                if (isClipActive && isPlaying && !isBuffering) {
                    // Sync time if drifted
                    const timeDiff = Math.abs(audioEl.currentTime - relativeTime);
                    if (timeDiff > 0.3) {
                        audioEl.currentTime = Math.max(0, relativeTime);
                    }

                    // Play if paused
                    if (audioEl.paused) {
                        audioEl.play().catch(() => { });
                    }
                } else {
                    // Pause when inactive or when playback is paused
                    if (!audioEl.paused) {
                        audioEl.pause();
                    }
                }
            }
        });
    }, [currentTime, isPlaying, allClips, playbackSpeed, isBuffering, isGlobalMuted, getEffectiveClipVolume]);

    // DEBUG: Periodic Audio Diagnostics
    useEffect(() => {
        const interval = setInterval(() => {
            if (!DEBUG_MEDIA || !isPlaying) return;
            allClips.forEach(clip => {
                if (clip.type !== 'video' && clip.type !== 'audio') return;
                const videoEl = videoRefs.current[clip.id];
                const enhancedKey = `enhanced-${clip.id}`;
                const enhancedEl = audioElementsRef.current[enhancedKey];
                const hasEnhancedConfig = clip.activeAudioSource === 'enhanced';
                const hasAudioSources = clip.audioSources?.length ?? 0;
                const isClipActive = currentTime >= clip.start && currentTime < clip.start + clip.duration;
                if (hasEnhancedConfig || enhancedEl) {
                    console.log(
                        `[AudioDebug] ${clip.name} | active=${isClipActive} | activeSource=${clip.activeAudioSource ?? 'none'} | sources=${hasAudioSources} | ` +
                        `video: muted=${videoEl?.muted} paused=${videoEl?.paused} | ` +
                        `enhanced: ${enhancedEl ? `muted=${enhancedEl.muted} paused=${enhancedEl.paused} vol=${enhancedEl.volume} ready=${enhancedEl.readyState} time=${enhancedEl.currentTime.toFixed(2)}` : 'NO ELEMENT'}`
                    );
                }
            });
        }, 2000);
        return () => clearInterval(interval);
    }, [isPlaying, allClips, currentTime]);

    // Use WebGPU (Check for support)
    const useWebGPU = false; // Disabled by default for stability as per context

    // Calculate Duration
    useEffect(() => {
        if (allClips.length === 0) setDuration(0);
        else {
            const max = Math.max(...allClips.map(c => c.start + c.duration));
            setDuration(Math.max(max, 10)); // Min 10s
        }
    }, [allClips]);

    // Playback Loop
    useEffect(() => {
        let animationFrameId: number;
        let lastReactUpdateTime = 0;
        const animate = () => {
            if (isPlayingRef.current) {
                const now = Date.now();
                const elapsedSinceStart = (now - playbackStartTime.current) / 1000;
                const newTime = playbackStartOffset.current + (elapsedSinceStart * playbackSpeedRef.current);
                if (newTime >= durationRef.current) {
                    setIsPlaying(false);
                    setCurrentTime(durationRef.current);
                    currentTimeRef.current = durationRef.current;
                } else {
                    currentTimeRef.current = newTime;

                    // Throttle React State Updates (~15fps for UI elements)
                    if (now - lastReactUpdateTime > 66) {
                        setCurrentTime(newTime);
                        lastReactUpdateTime = now;
                    }

                    // Directly update playhead DOM for 60fps smooth animation
                    if (playheadRef.current) {
                        playheadRef.current.style.transform = `translateX(${newTime * zoomRef.current}px)`;
                    }
                }
            }
            if (isPlayingRef.current || isScrubbing) {
                onTimeUpdate?.(currentTimeRef.current);
            }
            // Always redraw the canvas to allow APNGs/GIFs to animate even when video is paused.
            // drawCanvasFrameRef is set to the latest drawCanvasFrame function.
            if (drawCanvasFrameRef.current) {
                drawCanvasFrameRef.current();
            }
            animationFrameId = requestAnimationFrame(animate);
        };
        animationFrameId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationFrameId);
    }, [duration, onTimeUpdate, isScrubbing]); // Note: Hooks dependencies logic simplified for loop

    // Helper: Clip Update
    const handleClipUpdate = useCallback((clipId: string, updates: Partial<Clip>) => {
        onClipsChange(prev => prev.map(c => c.id === clipId ? { ...c, ...updates } : c));
    }, [onClipsChange]);

    const handleSelectClip = useCallback((clipId: string | null, element: HTMLElement | null) => {
        setSelectedClipId(clipId);
        onClipSelect?.(clipId ? allClips.find(c => c.id === clipId) || null : null);
    }, [allClips, onClipSelect, setSelectedClipId]);

    const handleDeleteClip = useCallback((clipId: string) => {
        onClipsChange(prev => {
            const clipToDelete = prev.find(c => c.id === clipId);
            if (!clipToDelete) return prev;

            const deletedDuration = clipToDelete.duration;
            const deletedStart = clipToDelete.start;
            const targetTrackId = clipToDelete.trackId;

            // Ripple delete: remove the clip, then shift subsequent clips on the same track to the left
            return prev
                .filter(c => c.id !== clipId)
                .map(c => {
                    // Only shift clips on the identical track that come AFTER the deleted clip's start time
                    if (c.trackId === targetTrackId && c.start >= deletedStart) {
                        return { ...c, start: Math.max(0, c.start - deletedDuration) };
                    }
                    return c;
                });
        });
        if (selectedClipId === clipId) setSelectedClipId(null);
    }, [onClipsChange, selectedClipId, setSelectedClipId]);

    // Split Clip
    const handleSplitClipAtTime = useCallback((clip: Clip, splitTime: number) => {
        if (isRestrictedMode) return;
        const relativeSplitTime = splitTime - clip.start;
        if (relativeSplitTime <= 0.1 || relativeSplitTime >= clip.duration - 0.1) return;

        const firstPartDuration = relativeSplitTime;
        const secondPartDuration = clip.duration - relativeSplitTime;

        onClipsChange(prev => {
            const newClips = prev.filter(c => c.id !== clip.id);
            const splitAbsTime = clip.start + firstPartDuration;
            const clip1 = {
                ...clip,
                id: crypto.randomUUID(),
                duration: firstPartDuration,
                // Keep only subtitles in the first half
                subtitles: filterAllSubtitleLanguages(clip.subtitles, clip.start, splitAbsTime),
            };
            const clip2 = {
                ...clip,
                id: crypto.randomUUID(),
                start: splitAbsTime,
                duration: secondPartDuration,
                trimStart: (clip.trimStart || 0) + firstPartDuration,
                // Keep only subtitles in the second half
                subtitles: filterAllSubtitleLanguages(clip.subtitles, splitAbsTime, splitAbsTime + secondPartDuration),
            };
            return [...newClips, clip1, clip2];
        }, true); // Save to history
    }, [onClipsChange]);

    // Add Clip Helpers
    const handleAddTextClip = () => {
        const targetTrack = tracks.find(t => t.id === 't1');
        if (!targetTrack) return;
        const newClip = createNewClip({
            type: 'text', name: "Text", trackId: targetTrack.id, start: currentTime,
            text: "Double-click to edit",
            style: { fontFamily: 'Arial, sans-serif', fontSize: 50, color: '#FFFFFF', textAlign: 'center', textBaseline: 'middle' },
        });
        onClipsChange(prev => [...prev, newClip]);
        setSelectedClipId(newClip.id);
    };

    const handleAddSynthe = () => {
        const targetTrack = tracks.find(t => t.id === 't1');
        if (!targetTrack) return;

        const defaultClipsWithUniqueIds = [
            { id: `synthe-name-${Date.now()}`, name: 'Nom', type: 'text', trackId: 't1', start: 0, duration: 5, originalDuration: 5, trimStart: 0, text: 'Nom Prénom', style: { fontFamily: 'Futura, sans-serif', fontSize: 42, color: '#FFFFFF', textAlign: 'left', textBaseline: 'middle' }, transform: null, opacity: 1, fadeInDuration: 0, fadeOutDuration: 0 } as any,
            { id: `synthe-title-${Date.now()}`, name: 'Titre', type: 'text', trackId: 't1', start: 0, duration: 5, originalDuration: 5, trimStart: 0, text: 'Titre de la personne', style: { fontFamily: 'Futura, sans-serif', fontSize: 24, color: '#CCCCCC', textAlign: 'left', textBaseline: 'middle' }, transform: { x: 0, y: 50, scale: 1, rotation: 0 }, opacity: 1, fadeInDuration: 0, fadeOutDuration: 0 } as any
        ];

        const newClip = createNewClip({
            type: 'synthe',
            name: "Synthé: Identité",
            trackId: targetTrack.id,
            start: currentTime,
            duration: 5,
            syntheData: { clips: defaultClipsWithUniqueIds, tracks: tracks }
        });
        onClipsChange(prev => [...prev, newClip]);
        setSelectedClipId(newClip.id);
    };

    const handleAddPanel = () => {
        const targetTrack = tracks.find(t => t.id === 't1');
        if (!targetTrack) return;

        const defaultClipsWithUniqueIds = [
            { id: `panel-bg-${Date.now()}`, name: 'Background', type: 'image', trackId: 'v1', start: 0, duration: 5, originalDuration: 5, trimStart: 0, url: 'https://medias.storystudio.cc/IntroOutro/BG.png', transform: { x: 0, y: 0, scale: 1, rotation: 0 }, opacity: 1, fadeInDuration: 0, fadeOutDuration: 0 } as any,
            { id: `panel-text-title-${Date.now()}`, name: 'Title', type: 'text', trackId: 't1', start: 0, duration: 5, originalDuration: 5, trimStart: 0, text: 'VOTRE TEXTE ICI', style: { fontFamily: 'Futura, sans-serif', fontSize: 80, color: '#0066FF', textAlign: 'center', textBaseline: 'middle' }, transform: { x: 0, y: 0, scale: 1, rotation: 0 }, opacity: 1, fadeInDuration: 0.5, fadeOutDuration: 0.5 } as any
        ];

        const newClip = createNewClip({
            type: 'panel',
            name: "Panel: Intercalaire",
            trackId: targetTrack.id,
            start: currentTime,
            duration: 5,
            syntheData: { clips: defaultClipsWithUniqueIds, tracks: tracks, backgroundColor: '#000000' }
        });
        onClipsChange(prev => [...prev, newClip]);
        setSelectedClipId(newClip.id);
    };

    const handleSmileySelect = useCallback(async (smiley: any) => {
        setIsSmileyPopupOpen(false);
        const targetTrack = tracks.find(t => t.id === 't1');
        if (!targetTrack) return;
        const isGif = smiley.download_url?.toLowerCase().endsWith('.gif');
        const newClip = createNewClip({
            type: isGif ? 'video' : 'image',
            name: smiley.name.replace(/\.(png|gif)$/, ''),
            url: smiley.download_url,
            trackId: targetTrack.id,
            start: currentTime,
            duration: 3,
            transform: { x: 0, y: 0, scale: 0.15, rotation: 0 }
        });
        onClipsChange(prev => [...prev, newClip]);
        setSelectedClipId(newClip.id);
    }, [tracks, currentTime, onClipsChange, setSelectedClipId]);

    // Handle Canvas Click (Subtitle Hit Test)
    const handleCanvasClick = useCallback((e: React.MouseEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        // Scale mouse coordinates to canvas resolution
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        if (activeSubtitleLanguage !== 'none') {
            const activeSubtitle = getActiveSubtitleAtTime(currentTime, allClips);
            if (activeSubtitle) {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.save();
                    ctx.font = `bold ${subtitleStyle.fontSize}px ${subtitleStyle.fontFamily}`;

                    // Re-calculate safe area
                    const safeArea = { x: 0, y: 0, width: canvas.width, height: canvas.height };
                    if (aspectRatio === '1:1') { const size = canvas.height; safeArea.width = size; safeArea.x = (canvas.width - size) / 2; }
                    else if (aspectRatio === '9:16') { const width = canvas.height * (9 / 16); safeArea.width = width; safeArea.x = (canvas.width - width) / 2; }

                    const padding = safeArea.width * ((subtitleStyle.padding ?? 5) / 100);
                    const maxWidth = safeArea.width - (padding * 2);

                    // Word wrap measure
                    const words = activeSubtitle.text.split(' ');
                    let lines: string[] = []; let currentLine = '';
                    for (const word of words) {
                        const testLine = currentLine === '' ? word : `${currentLine} ${word}`;
                        if (ctx.measureText(testLine).width > maxWidth && currentLine !== '') { lines.push(currentLine); currentLine = word; }
                        else { currentLine = testLine; }
                    }
                    lines.push(currentLine);

                    const lineHeight = subtitleStyle.fontSize * 1.2;
                    const totalHeight = lines.length * lineHeight;
                    const textBottomY = (safeArea.y + safeArea.height) - (safeArea.height * subtitleStyle.positionY / 100);
                    const textTopY = textBottomY - totalHeight;

                    // Check intersection (generous X bounds)
                    const boxLeft = safeArea.x + padding;
                    const boxRight = safeArea.x + safeArea.width - padding;

                    if (y >= textTopY && y <= textBottomY + 10 && x >= boxLeft && x <= boxRight) { // +10 padding bottom
                        onSubtitleClick?.(activeSubtitle);
                    }
                    ctx.restore();
                }
            }
        }
    }, [allClips, activeSubtitleLanguage, currentTime, subtitleStyle, aspectRatio, onSubtitleClick, getActiveSubtitleAtTime]);
    // Ref to hold the latest drawCanvasFrame function for the animation loop
    const drawCanvasFrameRef = useRef<(() => Promise<void>) | null>(null);

    // Canvas Rendering Logic
    const drawCanvasFrame = useCallback(async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        if (useWebGPU && webgpuRendererRef.current) {
            const allVisibleClips = allClips.filter(c => {
                const track = tracks.find(t => t.id === c.trackId);
                return track?.isVisible && currentTime >= c.start && currentTime < c.start + c.duration;
            });
            await webgpuRendererRef.current.render(
                currentTime,
                allVisibleClips,
                tracks,
                videoRefs,
                imageRefs,
                scaleMode || 'fill'
            );
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const allVisibleClips = allClips.filter(c => {
            const track = tracks.find(t => t.id === c.trackId);
            return track?.isVisible && currentTime >= c.start && currentTime < c.start + c.duration;
        });

        const overlayClips = allVisibleClips.filter(c => c.trackId === 't1');
        const contentClips = allVisibleClips.filter(c => c.trackId !== 't1');
        const activeSplitClip = contentClips.find(c => c.trackId === 's1');

        // Check if at least one video clip is ready to render
        const hasReadyVideoClip = contentClips.some(clip => {
            if (clip.type !== 'video') return true;
            const videoEl = videoRefs.current[clip.id];
            return videoEl && (videoEl as any).readyState >= 2;
        });

        if (contentClips.length === 0 || hasReadyVideoClip) {
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        const renderSingleClip = (clip: Clip) => {
            let alpha = clip.opacity;
            if (clip.fadeInDuration > 0 && currentTime < clip.start + clip.fadeInDuration) {
                alpha *= (currentTime - clip.start) / clip.fadeInDuration;
            }
            if (clip.fadeOutDuration > 0 && currentTime > clip.start + clip.duration - clip.fadeOutDuration) {
                alpha *= ((clip.start + clip.duration) - currentTime) / clip.fadeOutDuration;
            }
            if (alpha <= 0) return;

            ctx.save();
            ctx.globalAlpha = Math.max(0, Math.min(1, alpha));

            if (clip.type === 'video' || clip.type === 'image') {
                const mediaEl = clip.type === 'video' ? videoRefs.current[clip.id] : imageRefs.current[clip.id];
                if (mediaEl) {
                    const isReady = clip.type === 'video' ? (mediaEl as any).readyState >= 2 : (mediaEl as any).complete;
                    const mediaWidth = clip.type === 'video' ? (mediaEl as any).videoWidth : (mediaEl as any).naturalWidth;
                    const mediaHeight = clip.type === 'video' ? (mediaEl as any).videoHeight : (mediaEl as any).naturalHeight;

                    if (isReady && mediaWidth > 0 && mediaHeight > 0 && clip.transform) {
                        const mediaAspect = mediaWidth / mediaHeight;
                        const canvasAspect = canvas.width / canvas.height;
                        let drawWidth, drawHeight;

                        const isFill = (scaleMode || 'fill') === 'fill';
                        if (isFill) {
                            if (mediaAspect > canvasAspect) {
                                drawHeight = canvas.height;
                                drawWidth = drawHeight * mediaAspect;
                            } else {
                                drawWidth = canvas.width;
                                drawHeight = drawWidth / mediaAspect;
                            }
                        } else {
                            if (mediaAspect > canvasAspect) {
                                drawWidth = canvas.width;
                                drawHeight = drawWidth / mediaAspect;
                            } else {
                                drawHeight = canvas.height;
                                drawWidth = drawHeight / mediaAspect;
                            }
                        }
                        const x = (canvas.width - drawWidth) / 2;
                        const y = (canvas.height - drawHeight) / 2;

                        let animationScale = 1.0;
                        // Removed hardcoded bounce animation because it caused scale=0 
                        // when a clip was just inserted at the current paused playhead.

                        ctx.save();
                        ctx.translate(canvas.width / 2, canvas.height / 2);
                        ctx.rotate((clip.transform.rotation * Math.PI) / 180);
                        ctx.scale(clip.transform.scale * animationScale, clip.transform.scale * animationScale);
                        ctx.translate(clip.transform.x, clip.transform.y);
                        ctx.translate(-canvas.width / 2, -canvas.height / 2);
                        ctx.drawImage(mediaEl, x, y, drawWidth, drawHeight);

                        // Draw Image Mask (Nano Banana)
                        if (clip.activeImageMask) {
                            let maskImg = nestedImageCache.current[clip.activeImageMask];
                            if (!maskImg) {
                                maskImg = new Image();
                                maskImg.crossOrigin = 'anonymous';
                                maskImg.src = clip.activeImageMask;
                                nestedImageCache.current[clip.activeImageMask] = maskImg;
                            }
                            if (maskImg.complete && maskImg.naturalWidth > 0) {
                                // Draw the mask exactly to the same bounds as the video
                                ctx.drawImage(maskImg, x, y, drawWidth, drawHeight);
                            }
                        }

                        ctx.restore();
                    }
                }
            } else if (clip.type === 'text' && clip.text && clip.style && clip.transform) {
                ctx.save();
                ctx.translate(canvas.width / 2, canvas.height / 2);
                ctx.rotate((clip.transform.rotation * Math.PI) / 180);
                ctx.scale(clip.transform.scale, clip.transform.scale);
                ctx.translate(clip.transform.x, clip.transform.y);
                ctx.translate(-canvas.width / 2, -canvas.height / 2);
                ctx.font = `${clip.style.fontSize}px ${clip.style.fontFamily}`;
                ctx.fillStyle = clip.style.color;
                ctx.textAlign = clip.style.textAlign;
                ctx.textBaseline = clip.style.textBaseline;

                const lines = clip.text.split('\n');
                const lineHeight = clip.style.fontSize * 1.2;
                const totalHeight = lines.length * lineHeight;
                let startY = canvas.height / 2;

                if (clip.style.textBaseline === 'middle') {
                    startY -= (totalHeight - lineHeight) / 2;
                } else if (clip.style.textBaseline === 'bottom') {
                    startY -= (totalHeight - lineHeight);
                }

                for (let i = 0; i < lines.length; i++) {
                    ctx.fillText(lines[i], canvas.width / 2, startY + (i * lineHeight));
                }
                ctx.restore();
            } else if (clip.type === 'synthe' || (clip.type === 'panel' && clip.syntheData)) {
                ctx.save();

                // Panel background: draw BEFORE root transform so it always fills the full canvas
                if (clip.type === 'panel' && clip.syntheData?.backgroundColor) {
                    ctx.fillStyle = clip.syntheData.backgroundColor;
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                }

                // Apply root transform ONLY to internal clips, not the background
                ctx.translate(canvas.width / 2, canvas.height / 2);
                if (clip.transform) {
                    ctx.rotate((clip.transform.rotation * Math.PI) / 180);
                    ctx.scale(clip.transform.scale, clip.transform.scale);
                    ctx.translate(clip.transform.x, clip.transform.y);
                }
                ctx.translate(-canvas.width / 2, -canvas.height / 2);

                const localTime = currentTime - clip.start;
                if (localTime >= 0 && localTime <= clip.duration) {
                    const internalClips = clip.syntheData?.clips || [];
                    const sortedInternal = [...internalClips].sort((a, b) => (a.type === 'text' ? 1 : 0) - (b.type === 'text' ? 1 : 0));
                    sortedInternal.forEach(internalClip => {
                        if (internalClip.type === 'image' && internalClip.url) {
                            let img = nestedImageCache.current[internalClip.id];
                            if (!img) {
                                img = new Image(); img.src = internalClip.url; nestedImageCache.current[internalClip.id] = img;
                            }
                            if (img && img.complete && localTime >= internalClip.start && localTime < internalClip.start + internalClip.duration && internalClip.transform) {
                                ctx.save();
                                ctx.translate(canvas.width / 2, canvas.height / 2);
                                ctx.rotate((internalClip.transform.rotation * Math.PI) / 180);
                                ctx.scale(internalClip.transform.scale, internalClip.transform.scale);
                                ctx.translate(internalClip.transform.x, internalClip.transform.y);
                                ctx.translate(-canvas.width / 2, -canvas.height / 2);
                                ctx.drawImage(img, (canvas.width - img.naturalWidth) / 2, (canvas.height - img.naturalHeight) / 2);
                                ctx.restore();
                            }
                        }
                        if (internalClip.type === 'text' && internalClip.text) {
                            if (localTime >= internalClip.start && localTime < internalClip.start + internalClip.duration && internalClip.style && internalClip.transform) {
                                ctx.save();
                                ctx.translate(canvas.width / 2, canvas.height / 2);
                                ctx.rotate((internalClip.transform.rotation * Math.PI) / 180);
                                ctx.scale(internalClip.transform.scale, internalClip.transform.scale);
                                ctx.translate(internalClip.transform.x, internalClip.transform.y);
                                ctx.translate(-canvas.width / 2, -canvas.height / 2);
                                ctx.font = `${internalClip.style.fontSize}px ${internalClip.style.fontFamily}`;
                                ctx.fillStyle = internalClip.style.color;
                                ctx.textAlign = internalClip.style.textAlign;
                                ctx.textBaseline = internalClip.style.textBaseline;

                                const lines = internalClip.text.split('\n');
                                const lineHeight = internalClip.style.fontSize * 1.2;
                                const totalHeight = lines.length * lineHeight;
                                let startY = canvas.height / 2;

                                if (internalClip.style.textBaseline === 'middle') {
                                    startY -= (totalHeight - lineHeight) / 2;
                                } else if (internalClip.style.textBaseline === 'bottom') {
                                    startY -= (totalHeight - lineHeight);
                                }

                                for (let i = 0; i < lines.length; i++) {
                                    ctx.fillText(lines[i], canvas.width / 2, startY + (i * lineHeight));
                                }
                                ctx.restore();
                            }
                        }
                    });
                }
                ctx.restore();
            }
            ctx.restore();
        };

        const renderClipsWithCamera = (clipsToRender: Clip[], allContentClips: Clip[]) => {
            ctx.save();
            const currentZoomClip = allContentClips.find(c => c.type === 'zoom' && currentTime >= c.start && currentTime < c.start + c.duration);
            if (currentZoomClip && currentZoomClip.transform && currentZoomClip.endTransform) {
                const { transform: defaultTransform, endTransform: targetTransform, duration, start } = currentZoomClip;
                const timeIntoClip = currentTime - start;
                const animationTime = 1.0;
                const inDuration = Math.min(animationTime, duration / 2);
                const outStartTime = duration - inDuration;
                let progress = 0;
                if (timeIntoClip < inDuration) progress = easeInOutCubic(timeIntoClip / inDuration);
                else if (timeIntoClip > outStartTime && outStartTime > inDuration) progress = 1 - easeInOutCubic((timeIntoClip - outStartTime) / inDuration);
                else if (timeIntoClip >= inDuration && timeIntoClip <= outStartTime) progress = 1;

                const camX = defaultTransform.x + (targetTransform.x - defaultTransform.x) * progress;
                const camY = defaultTransform.y + (targetTransform.y - defaultTransform.y) * progress;
                const camScale = defaultTransform.scale + (targetTransform.scale - defaultTransform.scale) * progress;
                const camRot = defaultTransform.rotation + (targetTransform.rotation - defaultTransform.rotation) * progress;

                ctx.translate(canvas.width / 2, canvas.height / 2);
                if (camScale !== 0) ctx.scale(1 / camScale, 1 / camScale);
                ctx.rotate(camRot * Math.PI / 180);
                ctx.translate(-canvas.width / 2 - camX, -canvas.height / 2 - camY);
            }

            const sortedClips = [...clipsToRender].sort((a, b) => tracks.findIndex(t => t.id === b.trackId) - tracks.findIndex(t => t.id === a.trackId));
            for (const clip of sortedClips) {
                renderSingleClip(clip);
            }
            ctx.restore();
        };

        if (activeSplitClip) {
            const animationDuration = 1.0;
            const timeIntoClip = currentTime - activeSplitClip.start;
            const timeBeforeEnd = (activeSplitClip.start + activeSplitClip.duration) - currentTime;
            let progress = 1.0;
            if (timeIntoClip < animationDuration) progress = timeIntoClip / animationDuration;
            else if (timeBeforeEnd < animationDuration) progress = timeBeforeEnd / animationDuration;
            const finalProgress = easeInOutCubic(progress);

            const clipsForLeft = contentClips.filter(c => c.id !== activeSplitClip.id && c.trackId !== 's1');
            ctx.save();
            const leftScale = 1.0 - (0.05 * finalProgress);
            const leftTranslateX = (-canvas.width / 4) * finalProgress;
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.scale(leftScale, leftScale);
            ctx.translate(-canvas.width / 2 + leftTranslateX, -canvas.height / 2);
            renderClipsWithCamera(clipsForLeft, contentClips);
            ctx.restore();

            ctx.save();
            const panelLeadingEdgeX = canvas.width - (canvas.width / 2) * finalProgress;
            const rightClipPath = new Path2D();
            rightClipPath.rect(panelLeadingEdgeX, 0, canvas.width - panelLeadingEdgeX, canvas.height);
            ctx.clip(rightClipPath);
            const contentTranslationX = panelLeadingEdgeX - (canvas.width / 2);
            ctx.translate(contentTranslationX, 0);
            const rightScale = 1.0 - (0.05 * finalProgress);
            const rightTranslateX = (canvas.width / 4) * finalProgress;
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.scale(rightScale, rightScale);
            ctx.translate(-canvas.width / 2 + rightTranslateX, -canvas.height / 2);
            renderClipsWithCamera([activeSplitClip], contentClips);
            ctx.restore();
        } else {
            renderClipsWithCamera(contentClips, contentClips);
        }

        const sortedOverlayClips = [...overlayClips].sort((a, b) => tracks.findIndex(t => t.id === b.trackId) - tracks.findIndex(t => t.id === a.trackId));
        for (const clip of sortedOverlayClips) {
            renderSingleClip(clip);
        }

        const safeArea = { x: 0, y: 0, width: canvas.width, height: canvas.height };
        if (aspectRatio === '1:1') { const size = canvas.height; safeArea.width = size; safeArea.x = (canvas.width - size) / 2; }
        else if (aspectRatio === '9:16') { const width = canvas.height * (9 / 16); safeArea.width = width; safeArea.x = (canvas.width - width) / 2; }

        if (activeSubtitleLanguage !== 'none') {
            const activeSubtitle = getActiveSubtitleAtTime(currentTime, allVisibleClips);
            if (activeSubtitle) {
                ctx.save();
                ctx.font = `bold ${subtitleStyle.fontSize}px ${subtitleStyle.fontFamily}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'bottom';
                const padding = safeArea.width * ((subtitleStyle.padding ?? 5) / 100);
                const maxWidth = safeArea.width - (padding * 2);
                const words = activeSubtitle.text.split(' ');
                let lines: string[] = []; let currentLine = '';
                for (const word of words) {
                    const testLine = currentLine === '' ? word : `${currentLine} ${word}`;
                    if (ctx.measureText(testLine).width > maxWidth && currentLine !== '') { lines.push(currentLine); currentLine = word; }
                    else { currentLine = testLine; }
                }
                lines.push(currentLine);
                const lineHeight = subtitleStyle.fontSize * 1.2;
                const x = safeArea.x + safeArea.width / 2;
                let y = (safeArea.y + safeArea.height) - (safeArea.height * subtitleStyle.positionY / 100);
                if (subtitleStyle.shadowType === 'shadow') { ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = 6; ctx.shadowOffsetX = 3; ctx.shadowOffsetY = 3; }
                else { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0; }
                ctx.fillStyle = subtitleStyle.color;
                if (subtitleStyle.shadowType === 'outline') { ctx.strokeStyle = '#000000'; ctx.lineWidth = subtitleStyle.outlineWidth ?? Math.max(1.5, subtitleStyle.fontSize * 0.05); ctx.lineJoin = 'round'; ctx.miterLimit = 2; }
                for (let i = 0; i < lines.length; i++) {
                    const line = lines[i];
                    const currentY = y - ((lines.length - 1 - i) * lineHeight);
                    if (subtitleStyle.shadowType === 'outline') {
                        ctx.strokeText(line, x, currentY);
                    }
                    ctx.fillText(line, x, currentY);
                }
                ctx.restore();
            }
        }

        if (aspectRatio !== '16:9') {
            ctx.save(); ctx.fillStyle = 'rgba(0, 0, 0, 0.4)'; ctx.beginPath();
            ctx.rect(0, 0, canvas.width, canvas.height);
            ctx.rect(safeArea.x, safeArea.y, safeArea.width, safeArea.height);
            ctx.fill('evenodd'); ctx.restore();
        }
        ctx.save(); ctx.strokeStyle = 'rgba(156, 163, 175, 0.5)'; ctx.lineWidth = 4;
        const inset = ctx.lineWidth / 2;
        ctx.strokeRect(safeArea.x + inset, safeArea.y + inset, safeArea.width - ctx.lineWidth, safeArea.height - ctx.lineWidth);
        ctx.restore();

        const selectedClip = allClips.find(c => c.id === selectedClipId);
        let activeClipBounds: DOMRect | null = null;
        if (selectedClip && (selectedClip.transform || selectedClip.endTransform) && allVisibleClips.includes(selectedClip)) {
            const tempCanvas = window.document.createElement('canvas'); tempCanvas.width = canvas.width; tempCanvas.height = canvas.height;
            const tempCtx = tempCanvas.getContext('2d')!;
            const transformToApply = selectedClip.type === 'zoom' ? selectedClip.endTransform : selectedClip.transform;

            if (transformToApply) {
                tempCtx.translate(canvas.width / 2, canvas.height / 2);
                tempCtx.rotate((transformToApply.rotation * Math.PI) / 180);
                tempCtx.scale(transformToApply.scale, transformToApply.scale);
                tempCtx.translate(transformToApply.x, transformToApply.y);
                tempCtx.translate(-canvas.width / 2, -canvas.height / 2);
            }

            let itemWidth = 0, itemHeight = 0;
            if (selectedClip.type === 'video' || selectedClip.type === 'image') {
                const mediaEl = selectedClip.type === 'video' ? videoRefs.current[selectedClip.id] : imageRefs.current[selectedClip.id];
                if (mediaEl) {
                    itemWidth = selectedClip.type === 'video' ? (mediaEl as any).videoWidth : (mediaEl as any).naturalWidth;
                    itemHeight = selectedClip.type === 'video' ? (mediaEl as any).videoHeight : (mediaEl as any).naturalHeight;
                }
            } else if (selectedClip.type === 'text') {
                itemWidth = ctx.measureText(selectedClip.text || '').width; itemHeight = selectedClip.style?.fontSize || 0;
            } else if (selectedClip.type === 'zoom') {
                itemWidth = canvas.width; itemHeight = canvas.height;
            }

            if (itemWidth > 0 && itemHeight > 0) {
                const mediaAspect = itemWidth / itemHeight; const canvasAspect = canvas.width / canvas.height;
                let drawWidth, drawHeight;
                if (mediaAspect > canvasAspect) { drawHeight = canvas.height; drawWidth = drawHeight * mediaAspect; }
                else { drawWidth = canvas.width; drawHeight = drawWidth / mediaAspect; }
                const x = (canvas.width - drawWidth) / 2; const y = (canvas.height - drawHeight) / 2;
                const corners = [{ x, y }, { x: x + drawWidth, y }, { x: x + drawWidth, y: y + drawHeight }, { x, y: y + drawHeight }];
                const transformMatrix = tempCtx.getTransform();
                const transformedCorners = corners.map(p => transformMatrix.transformPoint(p));
                const minX = Math.min(...transformedCorners.map(c => c.x)); const maxX = Math.max(...transformedCorners.map(c => c.x));
                const minY = Math.min(...transformedCorners.map(c => c.y)); const maxY = Math.max(...transformedCorners.map(c => c.y));

                const canvasContainer = canvasContainerRef.current;
                if (canvasContainer) {
                    const rect = canvasContainer.getBoundingClientRect(); const scale = rect.width / canvas.width;
                    activeClipBounds = new DOMRect(rect.x + minX * scale, rect.y + minY * scale, (maxX - minX) * scale, (maxY - minY) * scale);
                }
            }
        }
        setCanvasDimensions({ width: canvas.clientWidth, height: canvas.clientHeight, clipBounds: activeClipBounds, scale: canvas.clientWidth / canvas.width });
    }, [currentTime, allClips, tracks, selectedClipId, scaleMode, aspectRatio, subtitleStyle, activeSubtitleLanguage, getActiveSubtitleAtTime]);

    useEffect(() => {
        drawCanvasFrame();
    }, [drawCanvasFrame]);

    // Seek
    const handleSeek = useCallback((time: number) => {
        const newTime = Math.max(0, Math.min(time, duration || 0));
        setCurrentTime(newTime);
        if (isPlaying) {
            playbackStartOffset.current = newTime;
            playbackStartTime.current = Date.now();
        }
    }, [duration, isPlaying]);

    // Handlers needed for TimelineArea
    const handleClipDragStart = (e: React.MouseEvent, clip: Clip, action: string) => {
        e.stopPropagation();
        if (toolMode === 'cut' && action === 'move') {
            if (timelineContentRef.current) {
                const rect = timelineContentRef.current.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const splitTime = clickX / zoom;
                handleSplitClipAtTime(clip, splitTime);
            }
            return;
        }
        handleSelectClip(clip.id, e.currentTarget as HTMLElement);
        document.body.style.cursor = action === 'move' ? 'grabbing' : 'ew-resize';
        setClipDragState({ action, clipId: clip.id, startX: e.clientX, initialClip: { ...clip } });
    };

    const handleTrackDragStart = (e: React.MouseEvent, trackId: string) => {
        if (['t1', 's1', 'z1'].includes(trackId)) return;
        e.preventDefault();
        e.stopPropagation();
        setTrackDragState({ id: trackId });
    };

    // Drag Logic (MouseMove/Up) - kept in index.tsx as it covers the whole window
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!clipDragState) return;
            e.preventDefault();
            const event = e;
            const { action, initialClip, startX } = clipDragState;
            const dx = event.clientX - startX;
            const deltaSeconds = dx / zoom;
            let updates: Partial<Clip> = {};

            const snapThreshold = 10 / zoom;
            const snapPoints: number[] = [0];
            allClips.forEach(c => {
                if (c.id !== clipDragState.clipId) {
                    snapPoints.push(c.start); snapPoints.push(c.start + c.duration);
                }
            });

            const findSnap = (time: number) => {
                const closest = snapPoints.reduce((prev, curr) => Math.abs(curr - time) < Math.abs(prev - time) ? curr : prev);
                return Math.abs(closest - time) < snapThreshold ? closest : time;
            };

            if (action === 'move') {
                let newStart = Math.max(0, initialClip.start + deltaSeconds);
                let newEnd = newStart + initialClip.duration;
                const snappedStart = findSnap(newStart);
                const snappedEnd = findSnap(newEnd);

                if (Math.abs(snappedStart - newStart) <= Math.abs(snappedEnd - newEnd)) {
                    if (snappedStart !== newStart) newStart = snappedStart;
                } else {
                    if (snappedEnd !== newEnd) newStart = snappedEnd - initialClip.duration;
                }

                // Track Snapping/Target
                const tracksContainerRect = (tracksContainerRef.current as any)!.getBoundingClientRect();
                const yInTracks = event.clientY - tracksContainerRect.top;
                const trackIndex = Math.floor(yInTracks / TRACK_HEIGHT_WITH_MARGIN);
                let targetTrackId = initialClip.trackId;
                if (trackIndex >= 0 && trackIndex < tracks.length) {
                    const targetTrack = tracks[trackIndex];
                    if (targetTrack.accepts.includes(initialClip.type) && !['t1', 'z1'].includes(targetTrack.id)) {
                        targetTrackId = targetTrack.id;
                    }
                }

                // Collision Detection
                const clipsOnTargetTrack = allClips.filter(c => c.trackId === targetTrackId && c.id !== clipDragState.clipId);
                if (clipsOnTargetTrack.length > 0) {
                    const draggedDuration = initialClip.duration;
                    let finalStart = newStart;
                    for (const otherClip of clipsOnTargetTrack) {
                        const otherStart = otherClip.start;
                        const otherEnd = otherClip.start + otherClip.duration;
                        const draggedEnd = finalStart + draggedDuration;
                        if (finalStart < otherEnd && draggedEnd > otherStart) {
                            const centerOfDragged = finalStart + draggedDuration / 2;
                            const centerOfOther = otherStart + otherClip.duration / 2;
                            if (centerOfDragged < centerOfOther) finalStart = otherStart - draggedDuration;
                            else finalStart = otherEnd;
                        }
                    }
                    newStart = Math.max(0, finalStart);
                }

                updates.start = Math.max(0, newStart);
                if (targetTrackId !== initialClip.trackId) updates.trackId = targetTrackId;

                // Shift subtitles to follow the clip movement
                const startDelta = updates.start - initialClip.start;
                if (startDelta !== 0 && initialClip.subtitles) {
                    updates.subtitles = shiftAllSubtitleLanguages(initialClip.subtitles, startDelta);
                }

            } else if (action === 'resize-right') {
                const rawEnd = initialClip.start + initialClip.duration + deltaSeconds;
                const snappedEnd = findSnap(rawEnd);
                const newDuration = Math.max(0.5, snappedEnd - initialClip.start);
                if (initialClip.type === 'image' || initialClip.type === 'text' || initialClip.type === 'zoom') {
                    updates.duration = newDuration;
                } else {
                    updates.duration = Math.min(newDuration, initialClip.originalDuration - initialClip.trimStart);
                }
                handleSeek(initialClip.start + (updates.duration || initialClip.duration));

            } else if (action === 'resize-left') {
                const rawStart = initialClip.start + deltaSeconds;
                const snappedStart = findSnap(rawStart);
                if (initialClip.type === 'text' || initialClip.type === 'image' || initialClip.type === 'zoom') {
                    const newStart = Math.max(0, snappedStart);
                    const newDuration = initialClip.duration - (newStart - initialClip.start);
                    if (newDuration >= 0.5) { updates = { start: newStart, duration: newDuration }; handleSeek(newStart); }
                } else {
                    const newStart = Math.max(0, snappedStart);
                    const actualDelta = newStart - initialClip.start;
                    const newTrimStart = initialClip.trimStart + actualDelta;
                    const newDuration = initialClip.duration - actualDelta;
                    if (newDuration >= 0.5 && newTrimStart >= 0 && newTrimStart < initialClip.originalDuration) {
                        updates = { start: newStart, duration: newDuration, trimStart: newTrimStart };
                        // Shift subtitles: head trim moves start right, so subtitles shift left relative to new start
                        // But since subtitles use absolute times, we filter out those before new start
                        if (initialClip.subtitles && actualDelta !== 0) {
                            const newEnd = newStart + newDuration;
                            updates.subtitles = filterAllSubtitleLanguages(initialClip.subtitles, newStart, newEnd);
                        }
                        handleSeek(newStart);
                    }
                }
            }
            if (Object.keys(updates).length) handleClipUpdate(clipDragState.clipId, updates);
        };

        const handleMouseUp = () => {
            if (clipDragState) onClipsChange(p => [...p]);
            setClipDragState(null);
            document.body.style.cursor = 'default';
        };

        if (clipDragState) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [clipDragState, zoom, handleClipUpdate, handleSeek, tracks, allClips, onClipsChange]); // Add dependencies

    // Canvas Transform Logic
    useEffect(() => {
        if (!isTransforming) return;
        const { action, startX, startY, initialTransform } = isTransforming;
        let hasMoved = false;

        const handleMouseMove = (e: MouseEvent) => {
            e.preventDefault();
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasMoved = true;

            // Only update transform if we have a selected clip and we moved
            if (selectedClipId && hasMoved) {
                const scaleFactor = 1 / (canvasDimensions.scale || 1);
                let newTransform = { ...initialTransform };

                if (action === 'move') {
                    newTransform.x = initialTransform.x + (dx * scaleFactor);
                    newTransform.y = initialTransform.y + (dy * scaleFactor);
                } else if (action === 'rotate') {
                    // Simple rotation mapping for now
                    newTransform.rotation = (initialTransform.rotation + dx * 0.5) % 360;
                }

                handleClipUpdate(selectedClipId, { transform: newTransform });
            }
        };

        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            setIsTransforming(null);

            // if (!hasMoved && action === 'move') {
            //     setIsPlaying(p => !p);
            // }
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isTransforming, selectedClipId, handleClipUpdate, canvasDimensions]);

    // Resize Handle Logic
    const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const startY = e.clientY;
        const startHeight = previewSectionHeight || (containerRef.current ? containerRef.current.offsetHeight * 0.6 : 400);

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaY = moveEvent.clientY - startY;
            const newHeight = startHeight + deltaY;
            setPreviewSectionHeight(newHeight);
        };

        const handleMouseUp = () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'row-resize';
    }, [previewSectionHeight]);

    // Scrubbing Logic (Global Window Events) - moved to top level hook or effect

    useEffect(() => {
        if (!isScrubbing) return;

        const handleGlobalMouseMove = (e: MouseEvent) => {
            if (timelineContentRef.current) {
                const rect = timelineContentRef.current.getBoundingClientRect();
                const x = e.clientX - rect.left + timelineContentRef.current.scrollLeft;
                const newTime = Math.max(0, Math.min(x / zoom, duration));
                handleSeek(newTime);
            }
        };

        const handleGlobalMouseUp = () => {
            setIsScrubbing(false);
            document.body.style.cursor = 'default';
        };

        window.addEventListener('mousemove', handleGlobalMouseMove);
        window.addEventListener('mouseup', handleGlobalMouseUp);
        document.body.style.cursor = 'ew-resize';

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            document.body.style.cursor = 'default';
        };
    }, [isScrubbing, zoom, duration, handleSeek]);

    const handleScrubMouseDown = (e: React.MouseEvent) => {
        setIsScrubbing(true);
        if (timelineContentRef.current) {
            const rect = timelineContentRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left + timelineContentRef.current.scrollLeft;
            const newTime = Math.max(0, Math.min(x / zoom, duration));
            handleSeek(newTime);
        }
    };

    const handlePlayheadMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setIsScrubbing(true);
    };

    // Fullscreen Logic
    const [isFullscreen, setIsFullscreen] = useState(false);

    const handleToggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch((err) => console.warn('Fullscreen request failed:', err));
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    }, []);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Ignore if typing in an input or textarea
            if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;

            switch (e.code) {
                case 'Space':
                    e.preventDefault();
                    setIsPlaying(p => !p);
                    break;
                case 'Delete':
                case 'Backspace':
                    if (selectedClipId) {
                        e.preventDefault();
                        handleDeleteClip(selectedClipId);
                    }
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    handleSeek(Math.max(0, currentTimeRef.current - (1 / 30))); // 1 frame at 30fps
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    handleSeek(Math.min(durationRef.current, currentTimeRef.current + (1 / 30))); // 1 frame at 30fps
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedClipId, handleSeek, handleDeleteClip]);

    // Handle Drop
    const handleTimelineDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!timelineContentRef.current || !tracksContainerRef.current) return;

        // Calculate Drop Time
        const rect = timelineContentRef.current.getBoundingClientRect();
        const dropX = e.clientX - rect.left + timelineContentRef.current.scrollLeft;
        const dropTime = Math.max(0, dropX / zoom);

        // Calculate Target Track
        const tracksRect = tracksContainerRef.current.getBoundingClientRect();
        const yInTracks = e.clientY - tracksRect.top;
        const trackIndex = Math.floor(yInTracks / TRACK_HEIGHT_WITH_MARGIN);

        let targetTrackId: string | null = null;
        if (trackIndex >= 0 && trackIndex < tracks.length) {
            targetTrackId = tracks[trackIndex].id;
        }

        // Handle JSON drop data (from GeneratedVoiceItem, LibraryTab, etc.)
        if ((e.dataTransfer as any).types.includes('application/json')) {
            try {
                const jsonData = (e.dataTransfer as any).getData('application/json');
                if (jsonData) {
                    const dragData = JSON.parse(jsonData);

                    // Handle Audio (voiceover) drop
                    if (dragData.type === 'audio' && dragData.url) {
                        // Find appropriate audio track
                        let audioTrackId = targetTrackId;
                        const isValidTarget = audioTrackId && tracks.find(t => t.id === audioTrackId)?.accepts.includes('audio');

                        if (!isValidTarget) {
                            // Find first track that accepts audio (usually a1, a2, etc.)
                            audioTrackId = tracks.find(t => t.accepts.includes('audio') && !['t1', 's1', 'z1'].includes(t.id))?.id || null;
                        }

                        if (audioTrackId) {
                            const newClip = createNewClip({
                                type: 'audio',
                                name: dragData.name || 'Voiceover',
                                url: dragData.url,
                                trackId: audioTrackId,
                                start: dropTime,
                                duration: dragData.duration || dragData.originalDuration || 5,
                                originalDuration: dragData.originalDuration || dragData.duration || 5,
                            });
                            onClipsChange(prev => [...prev, newClip], true);
                            return; // Exit early after handling JSON drop
                        }
                    }

                    // Handle Video drop from library
                    if (dragData.type === 'video' && dragData.url) {
                        let videoTrackId = targetTrackId;
                        const isValidTarget = videoTrackId && tracks.find(t => t.id === videoTrackId)?.accepts.includes('video');

                        if (!isValidTarget) {
                            const v2 = tracks.find(t => t.id === 'v2');
                            if (v2 && v2.accepts.includes('video')) {
                                videoTrackId = 'v2';
                            } else {
                                videoTrackId = tracks.find(t => t.accepts.includes('video') && !['t1', 's1', 'z1'].includes(t.id))?.id || null;
                            }
                        }

                        if (videoTrackId) {
                            const newClip = createNewClip({
                                type: 'video',
                                name: dragData.name || 'Video',
                                url: dragData.url,
                                trackId: videoTrackId,
                                start: dropTime,
                                duration: dragData.duration || 10,
                                originalDuration: dragData.originalDuration || dragData.duration || 10,
                            });
                            onClipsChange(prev => [...prev, newClip], true);
                            return;
                        }
                    }

                    // Handle Image drop from library or generated masks
                    if (dragData.type === 'image' && dragData.url) {
                        let imageTrackId = targetTrackId;
                        // If no specific target, or target doesn't accept image, find one that does
                        if (!imageTrackId || !tracks.find(t => t.id === imageTrackId)?.accepts.includes('image')) {
                            // Prefer 't1' for image overlays if it accepts images
                            const t1 = tracks.find(t => t.id === 't1');
                            if (t1 && t1.accepts.includes('image')) {
                                imageTrackId = 't1';
                            } else {
                                // Fallback to any track accepting image (except subtitle/zoom)
                                imageTrackId = tracks.find(t => t.accepts.includes('image') && !['s1', 'z1'].includes(t.id))?.id || null;
                            }
                        }

                        if (imageTrackId) {
                            const newClip = createNewClip({
                                type: 'image',
                                name: dragData.name || 'Image',
                                url: dragData.url,
                                trackId: imageTrackId,
                                start: dropTime,
                                duration: dragData.duration || 5, // Default duration if not provided
                            });
                            if (dragData.transform && typeof dragData.transform === 'object') {
                                newClip.transform = {
                                    x: Number(dragData.transform.x) || 0,
                                    y: Number(dragData.transform.y) || 0,
                                    scale: Number(dragData.transform.scale) || 1,
                                    rotation: Number(dragData.transform.rotation) || 0,
                                };
                            }
                            onClipsChange(prev => [...prev, newClip], true);
                            return;
                        }
                    }

                    // Handle Synthe/Panel drop from library
                    if (dragData.type === 'synthe' || dragData.type === 'panel') {
                        let validTrackId = targetTrackId;
                        const targetTrack = tracks.find(t => t.id === validTrackId);

                        if (!targetTrack || !targetTrack.accepts.includes(dragData.type)) {
                            const t1 = tracks.find(t => t.id === 't1');
                            if (t1 && t1.accepts.includes(dragData.type)) {
                                validTrackId = 't1';
                            } else {
                                validTrackId = tracks.find(t => t.accepts.includes(dragData.type))?.id || null;
                            }
                        }

                        if (validTrackId) {
                            const newClip = createNewClip({
                                type: dragData.type,
                                name: dragData.name || (dragData.type === 'synthe' ? 'Synthé' : 'Panel'),
                                trackId: validTrackId,
                                start: dropTime,
                                duration: dragData.duration || 5,
                                originalDuration: dragData.originalDuration || dragData.duration || 5,
                                syntheData: dragData.syntheData,
                                url: dragData.url
                            });
                            onClipsChange(prev => [...prev, newClip], true);
                            return;
                        }
                    }
                }
            } catch (err) {
                console.warn('Failed to parse JSON drag data:', err);
            }
        }

        // Process Files (fallback for native file drops)
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files) as File[];
            const newClips: Clip[] = [];

            for (const file of files) {
                // Video
                if (file.type.startsWith('video/')) {
                    const videoUrl = URL.createObjectURL(file);
                    // Determine track: prioritize existing target if video, else prefer 'v2', then find first available
                    let videoTrackId = targetTrackId;
                    const isValidTarget = videoTrackId && tracks.find(t => t.id === videoTrackId)?.accepts.includes('video');

                    if (!isValidTarget) {
                        const v2 = tracks.find(t => t.id === 'v2');
                        if (v2 && v2.accepts.includes('video')) {
                            videoTrackId = 'v2';
                        } else {
                            videoTrackId = tracks.find(t => t.accepts.includes('video') && !['t1', 's1', 'z1'].includes(t.id))?.id || null;
                        }
                    }

                    if (videoTrackId) {
                        // Default duration, will load metadata later or we could wait here
                        const newClip = createNewClip({
                            type: 'video',
                            name: file.name,
                            url: videoUrl,
                            trackId: videoTrackId,
                            start: dropTime,
                            duration: 10, // Placeholder
                        });
                        newClips.push(newClip);
                        onVideoFileDropped && onVideoFileDropped(file, newClip);
                    }
                }
                // Image
                else if (file.type.startsWith('image/')) {
                    const imageUrl = URL.createObjectURL(file);
                    let imageTrackId = targetTrackId;
                    if (!imageTrackId || !tracks.find(t => t.id === imageTrackId)?.accepts.includes('image')) {
                        imageTrackId = tracks.find(t => t.accepts.includes('image') && !['t1', 's1', 'z1'].includes(t.id))?.id || null;
                    }

                    if (imageTrackId) {
                        const newClip = createNewClip({
                            type: 'image',
                            name: file.name,
                            url: imageUrl,
                            trackId: imageTrackId,
                            start: dropTime,
                            duration: 5,
                        });
                        newClips.push(newClip);
                        onVideoFileDropped && onVideoFileDropped(file, newClip);
                    }
                }
            }

            if (newClips.length > 0) {
                onClipsChange(prev => [...prev, ...newClips], true);
            }
        }
    }, [zoom, tracks, onClipsChange, onVideoFileDropped]);

    // Available Fonts
    const availableFonts = [
        { name: 'LVMH', family: 'LVMH, sans-serif' },
        { name: 'Arial', family: 'Arial, sans-serif' }
        // ...
    ];

    // Translation State (moved to PreviewArea, but state needed here? No, passed down setters)
    const [translationTargetLang, setTranslationTargetLang] = useState<string | null>(null);
    const [translationSourceLangs, setTranslationSourceLangs] = useState<Record<string, boolean>>({ en: true, fr: true });

    // Imperative Handle
    const handleZoomToFit = useCallback(() => {
        if (isRestrictedMode) return;
        if (!timelineContentRef.current || duration <= 0) return;
        const width = timelineContentRef.current.parentElement?.clientWidth || 1000;
        const requiredPps = width / (duration * 1.1);
        setZoom(Math.max(0.2, Math.min(500, requiredPps)));
    }, [duration, isRestrictedMode]);

    // Imperative Handle
    useImperativeHandle(ref, () => ({
        seek: handleSeek,
        getCurrentTime: () => currentTimeRef.current,
        pause: () => setIsPlaying(false),
        play: () => setIsPlaying(true),
        zoomToFit: handleZoomToFit,
        captureFrame: (): Promise<Blob | null> => {
            const canvas = canvasRef.current;
            if (!canvas) return Promise.resolve(null);
            return new Promise((resolve) => {
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.95);
            });
        }
    }));

    const containerClasses = `bg-[#09090b] text-white flex flex-col transition-all duration-300 font-sans selection:bg-blue-500/30 w-full h-full overflow-hidden`;



    return (
        <div ref={containerRef} className={containerClasses} id="timeline-player-container">
            <PreviewArea
                isPlaying={isPlaying}
                currentTime={currentTime}
                duration={duration}
                playbackSpeed={playbackSpeed}
                aspectRatio={aspectRatio}
                playerSize={isFullscreen ? 'fullscreen' : 'normal'}
                activeSubtitleLanguage={activeSubtitleLanguage}
                subtitleStyle={subtitleStyle}
                selectedClipId={selectedClipId}
                scaleMode={scaleMode}
                canvasDimensions={canvasDimensions}
                isBuffering={isBuffering}
                isMuted={isGlobalMuted}
                onToggleMute={() => setIsGlobalMuted((p) => !p)}

                montageVersions={montageVersions}
                currentVersionId={currentVersionId}
                canNavigateUp={canNavigateUp}
                navigationBreadcrumbs={navigationBreadcrumbs}

                allClips={allClips}
                tracks={tracks}

                canvasRef={canvasRef}
                canvasContainerRef={canvasContainerRef}

                editingTextClipId={editingTextClipId}
                editingTextValue={editingTextValue}
                editingFontFamily={editingFontFamily}
                availableFonts={availableFonts}

                onTogglePlay={() => {
                    if (!isPlaying && currentTime >= duration - 0.1) {
                        setCurrentTime(0);
                        currentTimeRef.current = 0; // Ensure immediate ref update for play loop
                        setIsPlaying(true);
                    } else {
                        setIsPlaying(p => !p);
                    }
                }}
                onSkipToStart={() => handleSeek(0)}
                onSkipToEnd={() => handleSeek(duration)}
                onSpeedChange={setPlaybackSpeed}
                onAspectRatioChange={onAspectRatioChange}
                onPlayerSizeChange={handleToggleFullscreen}
                onSelectVersion={onSelectVersion}
                onNavigateUp={onNavigateUp}
                onActiveSubtitleLanguageChange={onActiveSubtitleLanguageChange}
                onSeek={handleSeek}
                onSeekbarScrubStart={() => setIsSeekbarScrubbing(true)}
                onSeekbarScrubEnd={() => setIsSeekbarScrubbing(false)}

                onCanvasInteractionStart={(e, action, handle) => {
                    // Pass through to handler state
                    setIsTransforming({ action, handle, startX: e.clientX, startY: e.clientY, initialTransform: { ...allClips.find(c => c.id === selectedClipId)?.transform || { x: 0, y: 0, scale: 1, rotation: 0 } }, clipRect: new DOMRect() });
                    // (Simulated logic call, real impl needs full logic)
                }}
                onCanvasDoubleClick={(e) => {
                    const selected = allClips.find(c => c.id === selectedClipId);
                    if (selected?.type === 'text') {
                        setEditingTextClipId(selected.id);
                        setEditingTextValue(selected.text || '');
                        if (onTextOverlayDoubleClick) onTextOverlayDoubleClick();
                    }
                }}
                onCanvasWheel={() => { }}
                onCanvasHover={() => { }}

                setEditingTextClipId={setEditingTextClipId}
                setEditingTextValue={setEditingTextValue}
                setEditingFontFamily={setEditingFontFamily}
                handleTextEditComplete={() => {
                    if (editingTextClipId) handleClipUpdate(editingTextClipId, { text: editingTextValue });
                    setEditingTextClipId(null);
                }}

                translationTargetLang={translationTargetLang}
                setTranslationTargetLang={setTranslationTargetLang}
                translationSourceLangs={translationSourceLangs}
                setTranslationSourceLangs={setTranslationSourceLangs}
                onTranslateSubtitles={onTranslateSubtitles}
                onGenerateSubtitles={onGenerateSubtitles}
                translationsInProgress={translationsInProgress}

                previewSectionHeight={previewSectionHeight}
                projectLoadingState={projectLoadingState}
                onOpenExportMenu={onOpenExportMenu}
                onCanvasClick={handleCanvasClick}
                onSave={onSaveProject}
            />

            {/* Resize Handle - Hidden in fullscreen */}
            {!isFullscreen && (
                <div
                    className="h-2 bg-[#09090b] border-y border-white/5 cursor-row-resize hover:bg-white/10 transition-colors z-40 flex items-center justify-center shrink-0"
                    onMouseDown={handleResizeMouseDown}
                >
                    <div className="w-10 h-1 bg-white/10 rounded-full" />
                </div>
            )}

            {/* Timeline - Hidden in fullscreen */}
            {!isFullscreen && (
                <TimelineArea
                    tracks={tracks}
                    setTracks={setTracks}
                    allClips={allClips}
                    onClipsChange={onClipsChange}
                    zoom={zoom}
                    setZoom={setZoom}
                    currentTime={currentTime}
                    duration={duration}
                    selectedClipId={selectedClipId}
                    toolMode={toolMode}
                    setToolMode={setToolMode}

                    timelineScrollContainerRef={timelineScrollContainerRef}
                    timelineContentRef={timelineContentRef}
                    trackHeadersRef={trackHeadersRef}
                    tracksContainerRef={tracksContainerRef}
                    playheadRef={playheadRef}

                    onSeek={handleSeek}
                    onNavigateInto={onNavigateInto}
                    onClipSelect={handleSelectClip}
                    onDeleteClip={handleDeleteClip}
                    onAddTrack={() => {
                        const newTrack: Track = {
                            id: `track-${Date.now()}`,
                            label: '',
                            accepts: ['video', 'image', 'audio', 'text'],
                            isMagnetic: true,
                            isVisible: true,
                            isMuted: false,
                            volume: 1,
                        };
                        setTracks(prev => {
                            const newTracks = [...prev];
                            // Insert after Split Screen track (index 2)
                            newTracks.splice(2, 0, newTrack);
                            // Re-label non-system tracks
                            return newTracks.map((track, index) => {
                                if (['t1', 's1', 'z1'].includes(track.id)) return track;
                                return { ...track, label: `${index - 1}` };
                            });
                        });
                    }}
                    onDeleteTrack={(trackId) => {
                        if (['t1', 's1', 'z1'].includes(trackId)) return;
                        setTracks(prev => {
                            const filtered = prev.filter(t => t.id !== trackId);
                            return filtered.map((track, index) => {
                                if (['t1', 's1', 'z1'].includes(track.id)) return track;
                                return { ...track, label: `${index - 1}` };
                            });
                        });
                        // Remove clips on the deleted track
                        onClipsChange(prev => prev.filter(c => c.trackId !== trackId), true);
                    }}
                    onToggleTrackMagnetic={(trackId) => {
                        setTracks(prev => prev.map(t => t.id === trackId ? { ...t, isMagnetic: !t.isMagnetic } : t));
                    }}
                    onToggleTrackVisibility={(trackId) => {
                        setTracks(prev => prev.map(t => t.id === trackId ? { ...t, isVisible: !t.isVisible } : t));
                    }}
                    onToggleTrackMute={(trackId) => {
                        setTracks(prev => prev.map(t => t.id === trackId ? { ...t, isMuted: !t.isMuted } : t));
                    }}
                    onTrackVolumeChange={(trackId, volume) => {
                        setTracks(prev => prev.map(t => t.id === trackId ? { ...t, volume } : t));
                    }}

                    handleAddTextClip={handleAddTextClip}
                    handleAddSynthe={handleAddSynthe}
                    handleAddPanel={handleAddPanel}

                    undo={undo}
                    redo={redo}
                    historyIndex={historyIndex}
                    historyLength={historyLength}

                    onClipDragStart={handleClipDragStart}
                    onTrackDragStart={handleTrackDragStart}
                    onTimelineBackgroundMouseDown={() => handleSelectClip(null, null)}
                    onScrubMouseDown={handleScrubMouseDown}
                    onPlayheadMouseDown={handlePlayheadMouseDown}
                    onZoomIn={() => setZoom(z => Math.min(500, z * 1.2))}
                    onZoomOut={() => setZoom(z => Math.max(0.2, z / 1.2))}
                    onZoomToFit={handleZoomToFit}

                    isSmileyPopupOpen={isSmileyPopupOpen}
                    setIsSmileyPopupOpen={setIsSmileyPopupOpen}
                    onSmileySelect={handleSmileySelect}

                    dropIndicatorIndex={dropIndicatorIndex}
                    onFileDrop={handleTimelineDrop}
                    isRestrictedMode={isRestrictedMode}
                    onTextOverlayDoubleClick={onTextOverlayDoubleClick}
                />
            )}

            {/* Hidden Media Elements for Canvas Rendering */}
            <div className="hidden">
                {allClips.map((clip) => {
                    if (clip.type === 'video') {
                        return (
                            <video
                                key={clip.id}
                                ref={(el) => { if (el) videoRefs.current[clip.id] = el; }}
                                src={getClipLocalSourceUrl(clip) || ((clip.chunks && clip.chunks.length > 0) ? undefined : getClipPreviewUrl(clip))}
                                crossOrigin="anonymous"
                                preload="metadata"
                                style={{ display: 'none' }}
                                onWaiting={() => {
                                    const t = currentTimeRef.current;
                                    if (t >= clip.start && t < clip.start + clip.duration) {
                                        setIsBuffering(true);
                                    }
                                }}
                                onPlaying={() => {
                                    const t = currentTimeRef.current;
                                    if (t >= clip.start && t < clip.start + clip.duration) {
                                        setIsBuffering(false);
                                    }
                                }}
                                onCanPlay={() => {
                                    const t = currentTimeRef.current;
                                    if (t >= clip.start && t < clip.start + clip.duration) {
                                        setIsBuffering(false);
                                    }
                                }}
                                onSeeking={() => {
                                    const t = currentTimeRef.current;
                                    if (t >= clip.start && t < clip.start + clip.duration) {
                                        setIsBuffering(true);
                                    }
                                }}
                                onSeeked={() => {
                                    const t = currentTimeRef.current;
                                    if (t >= clip.start && t < clip.start + clip.duration) {
                                        setIsBuffering(false);
                                    }
                                    drawCanvasFrame();
                                }}
                                onError={(e) => {
                                    console.error(`[TimelinePlayer] Video error for clip ${clip.id}:`, e.currentTarget.error);
                                    const t = currentTimeRef.current;
                                    if (t >= clip.start && t < clip.start + clip.duration) {
                                        setIsBuffering(false);
                                    }

                                    // Fallback: if blob expired, try to use persistent URL from audioSources
                                    if (clip.url.startsWith('blob:') && clip.audioSources) {
                                        const originalSource = clip.audioSources.find(s => s.type === 'original');
                                        if (originalSource && originalSource.url && !originalSource.url.startsWith('blob:')) {
                                            console.log(`[TimelinePlayer] Falling back to persistent URL: ${originalSource.url}`);
                                            e.currentTarget.src = originalSource.url;
                                        }
                                    }
                                }}
                                onLoadedMetadata={(e) => {
                                    const video = e.currentTarget;
                                    if (clip.duration === 0 || !clip.duration) {
                                        // Update clip duration if needed (though usually handled on drop)
                                    }
                                }}
                            />
                        );
                    } else if (clip.type === 'image') {
                        return (
                            <img
                                key={clip.id}
                                ref={(el) => { if (el) imageRefs.current[clip.id] = el; }}
                                src={clip.url}
                                crossOrigin="anonymous"
                                style={{ position: 'absolute', width: 1, height: 1, opacity: 0.01, pointerEvents: 'none', zIndex: -100 }}
                                alt={clip.name}
                            />
                        );
                    }
                    return null;
                })}
            </div>

        </div>
    );
});

export default TimelinePlayer;
